import { useEffect, useState } from 'react';
import { Bell, AlertTriangle, CheckCircle, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AppColors } from '../types/theme';
import { useFeedbackNotifications, type FeedbackUpdateNotification } from './useFeedbackNotifications';
import { FeedbackUpdateModal } from './FeedbackUpdateModal';

interface Props {
  enabled: boolean;
  colors: AppColors;
}

const STATUS_ICON: Record<FeedbackUpdateNotification['status'], typeof CheckCircle> = {
  open: Clock,
  in_progress: Clock,
  resolved: CheckCircle,
  closed: CheckCircle,
};

export function FeedbackNotificationsBell({ enabled, colors }: Props) {
  const { t, i18n } = useTranslation('common');
  const { notifications, markViewed, markAllViewed } = useFeedbackNotifications(enabled);
  const [isOpen, setIsOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<FeedbackUpdateNotification | null>(null);

  // Auto-surface a modal for any critical-priority update the user hasn't
  // seen yet. We only show one at a time; after the user dismisses, the
  // next critical (if any) pops on the next render cycle.
  useEffect(() => {
    if (!enabled || activeModal) return;
    const nextCritical = notifications.find((n) => n.priority === 'critical');
    if (nextCritical) setActiveModal(nextCritical);
  }, [enabled, notifications, activeModal]);

  if (!enabled) return null;

  // Non-critical notifications appear in the bell dropdown. Critical ones
  // still appear here too (so the user can revisit them after dismissing
  // the modal), but they're flagged.
  const dropdownItems = notifications;
  const unreadCount = dropdownItems.length;

  const handleDismissModal = async () => {
    if (activeModal) await markViewed(activeModal.id);
    setActiveModal(null);
  };

  return (
    <>
      <div className="relative">
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="relative flex items-center justify-center p-2 rounded-lg transition-colors"
          style={{ backgroundColor: colors.hover_bg_color, color: colors.text_primary_color }}
          title={t('notifications.bellTooltip', { count: unreadCount })}
          aria-label={t('notifications.bellAria', { count: unreadCount })}
        >
          <Bell size={16} />
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
              style={{ backgroundColor: '#ef4444', color: '#ffffff' }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-50" onClick={() => setIsOpen(false)} />
            <div
              className="absolute right-0 mt-1 w-80 max-h-96 overflow-y-auto rounded-lg shadow-lg z-50"
              style={{ backgroundColor: colors.card_bg_color, border: `1px solid ${colors.border_color}` }}
            >
              <div
                className="flex items-center justify-between gap-2 px-4 py-2 sticky top-0"
                style={{ backgroundColor: colors.card_bg_color, borderBottom: `1px solid ${colors.border_color}` }}
              >
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: colors.text_secondary_color }}>
                  {t('notifications.title')}
                </span>
                {dropdownItems.length > 0 && (
                  <button
                    onClick={() => { void markAllViewed(); }}
                    className="text-xs font-medium hover:underline"
                    style={{ color: colors.primary_color }}
                  >
                    {t('notifications.markAllRead')}
                  </button>
                )}
              </div>
              {dropdownItems.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm" style={{ color: colors.text_secondary_color }}>
                  {t('notifications.empty')}
                </div>
              ) : (
                dropdownItems.map((n) => {
                  const Icon = STATUS_ICON[n.status];
                  const isCritical = n.priority === 'critical';
                  const lastNote = lastAdminNote(n.admin_notes);
                  return (
                    <div
                      key={n.id}
                      className="flex items-start transition-colors"
                      style={{ borderBottom: `1px solid ${colors.border_color}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.hover_bg_color)}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          setActiveModal(n);
                        }}
                        className="flex-1 min-w-0 text-left pl-4 py-3 flex items-start gap-2"
                      >
                        {isCritical ? (
                          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }} />
                        ) : (
                          <Icon size={16} className="flex-shrink-0 mt-0.5" style={{ color: colors.primary_color }} />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate" style={{ color: colors.text_primary_color }}>
                              {n.title}
                            </span>
                          </div>
                          <div className="text-xs mt-0.5" style={{ color: colors.text_secondary_color }}>
                            {t(`notifications.status.${n.status}`)} · {formatRelative(n.updated_at, i18n.language)}
                          </div>
                          {lastNote && (
                            <div className="text-xs mt-1 line-clamp-2" style={{ color: colors.text_secondary_color }}>
                              {lastNote}
                            </div>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={() => { void markViewed(n.id); }}
                        className="flex-shrink-0 p-2 mt-1 mr-1 rounded-lg"
                        style={{ color: colors.text_secondary_color }}
                        title={t('notifications.dismiss')}
                        aria-label={t('notifications.dismiss')}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
      {activeModal && (
        <FeedbackUpdateModal
          notification={activeModal}
          colors={colors}
          onDismiss={handleDismissModal}
        />
      )}
    </>
  );
}

/** Pull the latest "[timestamp email] message" line out of admin_notes. */
function lastAdminNote(notes: string): string {
  if (!notes) return '';
  const lines = notes.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return '';
  const last = lines[lines.length - 1];
  // Strip a leading [YYYY-MM-DD HH:MM email] prefix if present.
  const stripped = last.replace(/^\[[^\]]+\]\s*/, '');
  return stripped;
}

function formatRelative(iso: string, locale: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const mins = Math.round(ms / 60_000);
  if (mins < 1) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-0, 'minute');
  if (mins < 60) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-mins, 'minute');
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-hrs, 'hour');
  const days = Math.round(hrs / 24);
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-days, 'day');
}
